"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { DayButton, DayPicker, getDefaultClassNames } from "react-day-picker";
import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "dropdown",
  fixedWeeks = true,
  buttonVariant = "ghost",
  size = "default",
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"];
  size?: "default" | "lg";
}) {
  const defaultClassNames = getDefaultClassNames();
  const sizeClasses = {
    default: "[--cell-size:--spacing(8)]",
    lg: "[--cell-size:--spacing(12)]"
  };
  return <DayPicker showOutsideDays={showOutsideDays} fixedWeeks={fixedWeeks} className={cn("group/calendar p-3 rounded-xl", sizeClasses[size], String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`, String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`, className)} captionLayout={captionLayout} formatters={{
    formatMonthDropdown: date => date.toLocaleString("default", {
      month: "short"
    }),
    ...formatters
  }} classNames={{
    root: cn("w-fit h-auto", defaultClassNames.root),
    months: cn("flex gap-4 flex-col md:flex-row relative h-auto", defaultClassNames.months),
    month: cn("flex flex-col w-full gap-4 h-auto", defaultClassNames.month),
    nav: cn("flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between pointer-events-none", defaultClassNames.nav),
    button_previous: cn(buttonVariants({
      variant: buttonVariant
    }), "size-(--cell-size) aria-disabled:opacity-50 p-0 select-none rounded-full border border-gray-200 w-9 h-9 ml-2 pointer-events-auto", defaultClassNames.button_previous),
    button_next: cn(buttonVariants({
      variant: buttonVariant
    }), "size-(--cell-size) aria-disabled:opacity-50 p-0 select-none rounded-full border border-gray-200 w-9 h-9 mr-2 pointer-events-auto", defaultClassNames.button_next),
    month_caption: cn(
    // Match the prev/next button height (w-9 h-9) so dropdowns and
    // chevrons sit on the same baseline.
    "flex items-center justify-center h-9 w-full px-(--cell-size)", defaultClassNames.month_caption),
    dropdowns: cn(
    // Mobile dropdowns wear pill borders, so they need breathing
    // room between them (gap-1.5). On desktop the borders are
    // stripped, so dropping the gap brings Jun + 2026 right next
    // to each other.
    "w-full flex items-center text-sm font-medium justify-center h-9 gap-1.5 md:gap-0", defaultClassNames.dropdowns),
    dropdown_root: cn("", defaultClassNames.dropdown_root),
    dropdown: cn("", defaultClassNames.dropdown),
    caption_label: cn("select-none font-medium", size === "lg" ? "text-base" : "text-sm", captionLayout === "label" ? "" : "rounded-md pl-2 pr-1 flex items-center gap-1 h-8 [&>svg]:text-muted-foreground [&>svg]:size-3.5", defaultClassNames.caption_label),
    table: "w-full h-auto border-collapse",
    weekdays: cn("flex mt-2", defaultClassNames.weekdays),
    weekday: cn("text-muted-foreground rounded-md flex-1 font-normal select-none", size === "lg" ? "text-sm" : "text-[0.8rem]", defaultClassNames.weekday),
    week: cn("flex w-full mt-2", defaultClassNames.week),
    week_number_header: cn("select-none w-(--cell-size)", defaultClassNames.week_number_header),
    week_number: cn("select-none text-muted-foreground", size === "lg" ? "text-sm" : "text-[0.8rem]", defaultClassNames.week_number),
    day: cn("relative w-full h-full p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md group/day aspect-square select-none", defaultClassNames.day),
    range_start: cn("rounded-l-md bg-accent", defaultClassNames.range_start),
    range_middle: cn("rounded-none", defaultClassNames.range_middle),
    range_end: cn("rounded-r-md bg-accent", defaultClassNames.range_end),
    today: cn("bg-accent text-accent-foreground rounded-md data-[selected=true]:rounded-none", defaultClassNames.today),
    outside: cn("text-muted-foreground aria-selected:text-muted-foreground", defaultClassNames.outside),
    disabled: cn("text-muted-foreground opacity-50", defaultClassNames.disabled),
    hidden: cn("invisible", defaultClassNames.hidden),
    ...classNames
  }} components={{
    Root: ({
      className: className_0,
      rootRef,
      ...props_0
    }) => {
      return <div data-slot="calendar" ref={rootRef} className={cn("h-auto", className_0)} {...props_0} />;
    },
    Chevron: ({
      className: className_1,
      orientation,
      ...props_1
    }) => {
      const iconSize = size === "lg" ? "size-5" : "size-4";
      if (orientation === "left") {
        return <ChevronLeftIcon className={cn(iconSize, className_1)} {...props_1} />;
      }
      if (orientation === "right") {
        return <ChevronRightIcon className={cn(iconSize, className_1)} {...props_1} />;
      }
      return <ChevronDownIcon className={cn(iconSize, className_1)} {...props_1} />;
    },
    DayButton: CalendarDayButton,
    Dropdown: ({
      options,
      value,
      onChange,
      "aria-label": ariaLabel
    }) => <CalendarDropdown options={options} value={value as number | undefined} onChange={onChange} ariaLabel={ariaLabel} />,
    WeekNumber: ({
      children,
      ...props_2
    }) => {
      return <td {...props_2}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>;
    },
    ...components
  }} {...props} data-sentry-element="DayPicker" data-sentry-component="Calendar" data-sentry-source-file="calendar.tsx" />;
}
function CalendarDayButton(t0) {
  const $ = _c(20);
  let className;
  let day;
  let modifiers;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      day,
      modifiers,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = day;
    $[3] = modifiers;
    $[4] = props;
  } else {
    className = $[1];
    day = $[2];
    modifiers = $[3];
    props = $[4];
  }
  const defaultClassNames = getDefaultClassNames();
  const ref = React.useRef(null);
  let t1;
  let t2;
  if ($[5] !== modifiers.focused) {
    t1 = () => {
      if (modifiers.focused) {
        ref.current?.focus();
      }
    };
    t2 = [modifiers.focused];
    $[5] = modifiers.focused;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  React.useEffect(t1, t2);
  const T0 = Button;
  const t3 = "ghost";
  const t4 = "icon";
  let t5;
  if ($[8] !== day.date) {
    t5 = day.date.toLocaleDateString();
    $[8] = day.date;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle;
  const t7 = modifiers.range_start;
  const t8 = modifiers.range_end;
  const t9 = modifiers.range_middle;
  const t10 = modifiers.outside;
  const t11 = cn("data-[selected-single=true]:bg-primary data-[selected-single=true]:hover:bg-primary  data-[selected-single=true]:text-primary-foreground data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50 dark:hover:text-accent-foreground flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 leading-none font-normal group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md data-[range-start=true]:rounded-l-md [&>span]:opacity-70", "data-[outside=true]:text-muted-foreground/50 data-[outside=true]:font-light", "[&>span]:text-sm", defaultClassNames.day, className);
  let t12;
  if ($[10] !== T0 || $[11] !== modifiers.outside || $[12] !== modifiers.range_end || $[13] !== modifiers.range_middle || $[14] !== modifiers.range_start || $[15] !== props || $[16] !== t11 || $[17] !== t5 || $[18] !== t6) {
    t12 = <T0 ref={ref} variant={t3} size={t4} data-day={t5} data-selected-single={t6} data-range-start={t7} data-range-end={t8} data-range-middle={t9} data-outside={t10} className={t11} {...props} data-sentry-element="Button" data-sentry-component="CalendarDayButton" data-sentry-source-file="calendar.tsx" />;
    $[10] = T0;
    $[11] = modifiers.outside;
    $[12] = modifiers.range_end;
    $[13] = modifiers.range_middle;
    $[14] = modifiers.range_start;
    $[15] = props;
    $[16] = t11;
    $[17] = t5;
    $[18] = t6;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  return t12;
}
type CalendarDropdownProps = {
  options?: {
    value: number;
    label: string;
    disabled?: boolean;
  }[];
  value?: number;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  ariaLabel?: string;
};
function CalendarDropdown(t0) {
  const $ = _c(24);
  const {
    options,
    value,
    onChange,
    ariaLabel
  } = t0;
  const [open, setOpen] = React.useState(false);
  let t1;
  if ($[0] !== options || $[1] !== value) {
    t1 = options?.find(o => o.value === value);
    $[0] = options;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const selected = t1;
  const selectedRef = React.useRef(null);
  let t2;
  let t3;
  if ($[3] !== open) {
    t2 = () => {
      if (open) {
        requestAnimationFrame(() => {
          selectedRef.current?.scrollIntoView({
            block: "center"
          });
        });
      }
    };
    t3 = [open];
    $[3] = open;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  React.useEffect(t2, t3);
  let t4;
  if ($[6] !== onChange) {
    t4 = next => {
      const synthetic = {
        target: {
          value: String(next)
        },
        currentTarget: {
          value: String(next)
        }
      } as unknown as React.ChangeEvent<HTMLSelectElement>;
      onChange?.(synthetic);
      setOpen(false);
    };
    $[6] = onChange;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleSelect = t4;
  const t5 = selected?.label;
  let t6;
  if ($[8] !== t5) {
    t6 = <span>{t5}</span>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ChevronDownIcon className="size-3.5 text-muted-foreground" data-sentry-element="ChevronDownIcon" data-sentry-source-file="calendar.tsx" />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== ariaLabel || $[12] !== t6) {
    t8 = <PopoverTrigger asChild={true} data-sentry-element="PopoverTrigger" data-sentry-source-file="calendar.tsx"><button type="button" aria-label={ariaLabel} className="inline-flex items-center gap-1.5 h-9 rounded-full border border-gray-200 px-3 text-sm font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 cursor-pointer md:h-8 md:rounded-md md:border-0 md:px-2 md:gap-1">{t6}{t7}</button></PopoverTrigger>;
    $[11] = ariaLabel;
    $[12] = t6;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== handleSelect || $[15] !== options || $[16] !== value) {
    t9 = options?.map(opt => {
      const isSelected = opt.value === value;
      return <button key={opt.value} ref={isSelected ? selectedRef : undefined} type="button" disabled={opt.disabled} onClick={() => handleSelect(opt.value)} className={cn("w-full text-left px-2 py-1.5 rounded-sm text-sm cursor-pointer", "hover:bg-accent hover:text-accent-foreground", isSelected && "bg-accent text-accent-foreground font-medium", opt.disabled && "opacity-50 cursor-not-allowed pointer-events-none")}>{opt.label}</button>;
    });
    $[14] = handleSelect;
    $[15] = options;
    $[16] = value;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = <PopoverContent align="start" sideOffset={4} className="w-40 p-1 max-h-72 overflow-y-auto" onWheel={_temp} data-sentry-element="PopoverContent" data-sentry-source-file="calendar.tsx">{t9}</PopoverContent>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== open || $[21] !== t10 || $[22] !== t8) {
    t11 = <Popover open={open} onOpenChange={setOpen} preventClickThrough={false} data-sentry-element="Popover" data-sentry-component="CalendarDropdown" data-sentry-source-file="calendar.tsx">{t8}{t10}</Popover>;
    $[20] = open;
    $[21] = t10;
    $[22] = t8;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
}
function _temp(e) {
  e.currentTarget.scrollTop = e.currentTarget.scrollTop + e.deltaY;
  e.stopPropagation();
}
export { Calendar, CalendarDayButton };